import {
  useInboxUnreadCount,
  useUserInfo,
  useUserStatistics,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { useAuth } from "../lib/auth";
import { FEATURE_POINTS_HIDDEN } from "../lib/feature-flags";

/** Derive first character for avatar initials. Mirrors web `deriveInitial`. */
function deriveInitial(user: { nikeName?: string | null; userName?: string } | undefined): string {
  const name = user?.nikeName?.trim() || user?.userName?.trim() || "";
  return name.slice(0, 1).toUpperCase() || "?";
}

/** Derive display name. Mirrors web `deriveDisplayName`. */
function deriveDisplayName(
  user: { nikeName?: string | null; userName?: string } | undefined,
  fallback: string,
): string {
  return user?.nikeName?.trim() || user?.userName?.trim() || fallback;
}

/**
 * Profile tab — full 1:1 port of `mini-profile.html`.
 *
 * Sections:
 * 1. Gradient hero: avatar initials · display name · VIP level · ID · Edit button
 * 2. Hero stats: wallet balance (RUB kopecks) · points (hidden by flag) · coupons (backend N/A → hidden)
 * 3. 4-column quick actions: топup / withdraw / coupon / invite
 * 4. Order center card: 4 status tiles with counts from useUserStatistics
 * 5. Settings list: address / favorites / inbox (with unread badge) / bill / bank / security
 * 6. Support list: TG support / help / guide
 * 7. Auth gate banner for signed-out users
 */
export function ProfilePage() {
  const { t } = useTranslation("profile");
  const { token: authToken, status: authStatus } = useAuth();

  const { data: user } = useUserInfo(authToken);
  const { data: stats } = useUserStatistics(authToken);
  const { data: inboxCount } = useInboxUnreadCount(authToken);

  const displayName = deriveDisplayName(user, t("guest_name"));
  const initials = deriveInitial(user);

  // VIP tier label — mirrors web profile-page-client logic
  const tierLabel = (() => {
    if (user?.promoteLevel != null) return `VIP ${user.promoteLevel}`;
    if (user?.level != null) return `VIP ${user.level}`;
    return null;
  })();

  // Wallet balance: user.amount is in the request currency (RUB kopecks on ru locale)
  const walletKopecks = user?.amount ?? null;
  const walletDisplay =
    walletKopecks != null ? formatPrice(walletKopecks, "ru-RU", "RUB") : "—";

  // Order statistics from useUserStatistics
  const waitPayment = stats?.waitPaymentOrderQuantity ?? null;
  const purchasing = null; // backend UserStatistics has no direct "purchasing" count — honest omission
  const waitConfirm = stats?.waitConfirmOrderQuantity ?? null;
  const totalOrders = stats?.orderQuantity ?? null;

  const unreadCount = inboxCount ?? 0;

  return (
    <TgPage tabbar>
      <TgHeader title={t("head.title")} back={false} right="⚙" borderless />

      {/* ── Profile hero gradient ───────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg,var(--color-primary),var(--color-primary-fill))",
          color: "#fff",
          padding: "18px 16px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circle */}
        <div
          style={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 140,
            height: 140,
            background: "rgba(255,210,63,.18)",
            borderRadius: "50%",
          }}
        />

        {/* Name row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
          <div
            style={{
              width: 54,
              height: 54,
              borderRadius: "50%",
              background: "rgba(255,255,255,.22)",
              display: "grid",
              placeItems: "center",
              fontFamily: "-apple-system,system-ui,sans-serif",
              fontWeight: 900,
              fontSize: 22,
              backdropFilter: "blur(4px)",
            }}
          >
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: 16,
                fontWeight: 800,
              }}
            >
              {displayName}
            </div>
            <div
              style={{
                fontFamily: "monospace",
                fontSize: 11,
                opacity: 0.85,
                marginTop: 2,
              }}
            >
              {tierLabel ? `${tierLabel} · ` : ""}
              {user?.id ? `ID ${user.id}` : "—"}
            </div>
          </div>
          {/* Edit button */}
          <button
            type="button"
            style={{
              background: "rgba(255,255,255,.18)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,.22)",
              padding: "5px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {t("edit_profile")}
          </button>
        </div>

        {/* Stats row: wallet · points (hidden by flag) · coupons (backend N/A → hidden) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: FEATURE_POINTS_HIDDEN ? "repeat(1,1fr)" : "repeat(2,1fr)",
            gap: 10,
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,.18)",
            position: "relative",
          }}
        >
          <div>
            <div
              style={{ fontSize: 9, opacity: 0.7, fontFamily: "monospace", letterSpacing: "0.04em" }}
            >
              {t("wallet_label")}
            </div>
            <div
              style={{
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: 14,
                fontWeight: 900,
                marginTop: 2,
              }}
            >
              {walletDisplay}
            </div>
          </div>
          {!FEATURE_POINTS_HIDDEN && (
            <div>
              <div
                style={{
                  fontSize: 9,
                  opacity: 0.7,
                  fontFamily: "monospace",
                  letterSpacing: "0.04em",
                }}
              >
                {t("points_label")}
              </div>
              <div
                style={{
                  fontFamily: "-apple-system,system-ui,sans-serif",
                  fontSize: 14,
                  fontWeight: 900,
                  marginTop: 2,
                }}
              >
                {user?.score != null ? `${user.score} ★` : "—"}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* ── 4-column quick actions ─────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
          <QuickAction icon="💳" label={t("mini.quick_topup")} to="/wallet" />
          <QuickAction icon="↗" label={t("mini.quick_withdraw")} to="/withdraw" />
          {/* coupons: backend has no coupon endpoint yet → navigate to wallet as placeholder */}
          <QuickAction icon="🎁" label={t("mini.quick_coupon")} to="/wallet" />
          <QuickAction icon="👥" label={t("mini.quick_invite")} to="/invite" />
        </div>

        {/* ── Order center card ──────────────────────────────────────────── */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            <strong style={{ fontSize: 12 }}>{t("mini.order_center")}</strong>
            {totalOrders != null && totalOrders > 0 && (
              <Link
                to={"/orders" as never}
                style={{
                  fontSize: 10,
                  color: "var(--color-primary-fill)",
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                {t("mini.order_all", { count: totalOrders })}
              </Link>
            )}
            {(totalOrders == null || totalOrders === 0) && (
              <Link
                to={"/orders" as never}
                style={{
                  fontSize: 10,
                  color: "var(--color-primary-fill)",
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                →
              </Link>
            )}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 4,
              padding: "12px 8px",
            }}
          >
            <OrderTile icon="⏱" label={t("mini.order_wait_payment")} count={waitPayment} urgent />
            <OrderTile icon="🛒" label={t("mini.order_purchasing")} count={purchasing} />
            <OrderTile icon="📷" label={t("mini.order_photo_check")} count={waitConfirm} warn />
            <OrderTile icon="✈" label={t("mini.order_in_transit")} count={null} />
          </div>
        </div>

        {/* ── Settings list ──────────────────────────────────────────────── */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <NavRow icon="📍" label={t("mini.nav_address")} to="/address" />
          <NavRow icon="❤" label={t("mini.nav_favorite")} to="/favorite" />
          <NavRow
            icon="📩"
            label={t("mini.nav_message")}
            to="/message"
            badge={unreadCount > 0 ? unreadCount : 0}
          />
          <NavRow icon="📊" label={t("mini.nav_bill")} to="/bill" />
          <NavRow icon="🏦" label={t("mini.nav_bank")} to="/wallet" />
          <NavRow icon="⚙" label={t("mini.nav_security")} to="/wallet" last />
        </div>

        {/* ── Support list ───────────────────────────────────────────────── */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          {/* TG support — external link */}
          <a
            href="https://t.me/kupikupi_support"
            target="_blank"
            rel="noreferrer"
            style={{
              display: "grid",
              gridTemplateColumns: "32px 1fr auto",
              gap: 10,
              padding: "12px 14px",
              alignItems: "center",
              borderBottom: "1px solid var(--color-border)",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <span style={{ fontSize: 18 }}>💬</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{t("mini.nav_support")}</span>
            <span style={{ fontSize: 10, color: "var(--color-primary-fill)", fontWeight: 700 }}>
              {t("mini.support_online")}
            </span>
          </a>
          <NavRow icon="❓" label={t("mini.nav_help")} to="/help" />
          <NavRow icon="🚀" label={t("mini.nav_guide")} to="/beginner-guide" last />
        </div>

        {/* ── Auth gate banner ───────────────────────────────────────────── */}
        {authStatus === "signed-out" && (
          <div
            style={{
              background: "var(--color-warning-soft)",
              border: "1px solid var(--color-warning)",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 11,
              color: "var(--color-warning-foreground)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>🔒</span>
            <span style={{ flex: 1 }}>{t("signin_banner")}</span>
            <Link
              to={"/login" as never}
              style={{
                background: "var(--color-text)",
                color: "#fff",
                padding: "5px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {t("signin_cta")}
            </Link>
          </div>
        )}
      </div>

      <TgBottomNav />
    </TgPage>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface QuickActionProps {
  icon: string;
  label: string;
  to: string;
}

function QuickAction({ icon, label, to }: QuickActionProps) {
  return (
    <Link
      to={to as never}
      style={{
        background: "#fff",
        border: "1px solid var(--color-border)",
        borderRadius: 10,
        padding: "12px 4px",
        textAlign: "center",
        textDecoration: "none",
        color: "inherit",
        display: "block",
      }}
    >
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 10, fontWeight: 700, marginTop: 4 }}>{label}</div>
    </Link>
  );
}

interface OrderTileProps {
  icon: string;
  label: string;
  /** null = no badge; 0 = no badge (zero means nothing to show) */
  count: number | null;
  /** CTA red badge color */
  urgent?: boolean;
  /** Yellow warning badge */
  warn?: boolean;
}

function OrderTile({ icon, label, count, urgent, warn }: OrderTileProps) {
  const showBadge = count != null && count > 0;
  const badgeBg = urgent
    ? "var(--color-cta)"
    : warn
      ? "var(--color-sticker-yellow, #f5c518)"
      : "var(--color-primary-fill)";
  const badgeFg = warn ? "var(--color-sticker-yellow-fg, #333)" : "#fff";

  return (
    <Link
      to={"/orders" as never}
      style={{
        textAlign: "center",
        textDecoration: "none",
        color: "inherit",
        position: "relative",
        display: "block",
      }}
    >
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>{label}</div>
      {showBadge && (
        <span
          style={{
            position: "absolute",
            top: 0,
            right: 8,
            background: badgeBg,
            color: badgeFg,
            fontSize: 8,
            padding: "1px 5px",
            borderRadius: 999,
            fontWeight: 800,
          }}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

interface NavRowProps {
  icon: string;
  label: string;
  to: string;
  /** Unread count badge (omit or 0 = no badge) */
  badge?: number | undefined;
  /** Last item — no bottom border */
  last?: boolean | undefined;
}

function NavRow({ icon, label, to, badge, last }: NavRowProps) {
  return (
    <Link
      to={to as never}
      style={{
        display: "grid",
        gridTemplateColumns: "32px 1fr auto",
        gap: 10,
        padding: "12px 14px",
        alignItems: "center",
        borderBottom: last ? "none" : "1px solid var(--color-border)",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
      {badge != null && badge > 0 ? (
        <span
          style={{
            display: "inline-block",
            background: "var(--color-cta)",
            color: "#fff",
            fontSize: 9,
            padding: "1px 7px",
            borderRadius: 999,
            fontWeight: 800,
          }}
        >
          {badge}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>›</span>
      )}
    </Link>
  );
}
